"use client";

import { useState } from "react";
import type { OwnerKeyState } from "@/lib/owner";
import { createBusiness, signIn } from "./actions";
import { CopyButton } from "./TileForms";
import { useFormAction } from "./useFormAction";

const btn = "inline-flex h-11 items-center justify-center rounded-[13px] px-5 text-[14.5px] font-medium text-white disabled:opacity-60";

/** one line of the printed form, same as the details form uses */
function Row({ id, label, error, children }: { id: string; label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="form-row">
      <label htmlFor={id} className="text-[13px] text-ink-soft">
        {label}
      </label>
      <div className="min-w-0">
        {children}
        {error && (
          <p role="alert" className="mt-1 text-sm text-alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

// signs in with the key that was just shown
function ContinueForm({ apiKey }: { apiKey: string }) {
  const [, onSubmit, pending] = useFormAction(signIn);
  return (
    <form onSubmit={onSubmit} className="flex">
      <input type="hidden" name="apiKey" value={apiKey} />
      <button type="submit" disabled={pending} className={`${btn} h-10 bg-stamp px-4 text-[13.5px]`}>
        Continue to setup
      </button>
    </form>
  );
}

export function Gate({ demo, ownerKey }: { demo: { name: string; apiKey: string }[]; ownerKey: OwnerKeyState }) {
  const [signInState, onSignIn, signingIn] = useFormAction(signIn);
  const [createState, onCreate, creating] = useFormAction(createBusiness);
  const [key, setKey] = useState("");

  return (
    <>
      <section aria-labelledby="signin">
        <h2 id="signin" className="text-[17px] font-semibold">
          Sign in with your API key
        </h2>
        <p className="mt-0.5 text-[13.5px] text-ink-soft">The same key your till sends in the x-api-key header.</p>
        <form onSubmit={onSignIn} className="mt-2">
          <Row id="apiKey" label="API key" error={signInState?.errors?.apiKey}>
            <input
              id="apiKey"
              name="apiKey"
              dir="ltr"
              autoComplete="off"
              spellCheck={false}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              aria-invalid={!!signInState?.errors?.apiKey}
              className="slip-input num"
              placeholder="ezr_..."
              required
            />
          </Row>
          <button type="submit" disabled={signingIn || !key.trim()} className={`${btn} mt-5 bg-stamp`}>
            {signingIn ? "Checking" : "Sign in"}
          </button>
        </form>

        {demo.length > 0 && (
          <details className="mt-5 rounded-[14px] border-2 border-dashed border-line">
            <summary className="flex min-h-11 cursor-pointer items-center px-3.5 text-sm font-medium">Use a demo business</summary>
            <ul>
              {demo.map((d) => (
                <li key={d.apiKey} className="border-t-2 border-dotted border-line">
                  <button
                    type="button"
                    onClick={() => setKey(d.apiKey)}
                    className="flex min-h-11 w-full items-center justify-between gap-3 px-3.5 text-start text-sm"
                  >
                    <span>{d.name}</span>
                    <span className="underline underline-offset-4">Fill in key</span>
                  </button>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      <hr className="perf" />

      <section aria-labelledby="create">
        <h2 id="create" className="text-[17px] font-semibold">
          Or set up a new business
        </h2>
        {createState?.ok && createState.apiKey ? (
          <div className="mt-3.5">
            <p className="text-[15px] font-medium">Your business is ready. Copy your API key now.</p>
            <p className="mt-1 text-[13.5px] text-ink-soft">We only show it once. Your till sends it with every sale.</p>
            <p className="num mt-3 rounded-[14px] border-2 border-dashed border-line p-3.5 text-sm break-all" data-testid="new-key">
              {createState.apiKey}
            </p>
            <div className="mt-3 flex gap-2">
              <CopyButton value={createState.apiKey} label="Copy key" />
              <ContinueForm apiKey={createState.apiKey} />
            </div>
          </div>
        ) : ownerKey !== "on" ? (
          <p className="mt-2 text-[13.5px] text-ink-soft">
            {ownerKey === "off"
              ? "Setting up a business needs the owner key. Set ADMIN_KEY on the server to turn it on."
              : "ADMIN_KEY is set but too short. Use 16 characters or more."}
          </p>
        ) : (
          <form onSubmit={onCreate} className="mt-2">
            <Row id="ownerKey" label="Owner key" error={createState?.errors?.ownerKey}>
              <input
                id="ownerKey"
                name="ownerKey"
                type="password"
                dir="ltr"
                autoComplete="current-password"
                aria-invalid={!!createState?.errors?.ownerKey}
                className="slip-input"
                required
              />
              <p className="mt-1 text-[13px] text-ink-soft">Only the owner of this service has it.</p>
            </Row>
            <Row id="name" label="Business name" error={createState?.errors?.name}>
              <input id="name" name="name" autoComplete="organization" placeholder="Your business name" maxLength={80} aria-invalid={!!createState?.errors?.name} className="slip-input" required />
            </Row>
            <Row id="nameAr" label="Name in Arabic">
              <input id="nameAr" name="nameAr" dir="rtl" lang="ar" placeholder="الاسم بالعربية" maxLength={80} className="slip-input" />
              <p className="mt-1 text-[13px] text-ink-soft">Optional, you can add it later.</p>
            </Row>
            <button type="submit" disabled={creating} className={`${btn} mt-5 bg-ink`}>
              {creating ? "Creating" : "Create business"}
            </button>
          </form>
        )}
      </section>
    </>
  );
}
